import { Component, OnInit, EventEmitter, Input, Output, DestroyRef, signal } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import * as _ from 'lodash';
import { BehaviorSubject } from 'rxjs';
import { ISelectOption } from 'src/app/models/selectOptions';
import { ISelectOptionGroups } from 'src/app/models/selectOptions/selectOptionGroups.interface';

import { FilterTypesEnum } from 'src/app/models/filters/filterTypes.enum';
import { combineLatest, debounceTime, distinctUntilChanged, filter, finalize, switchMap, tap } from 'rxjs';
import { createDepartmentVerticalFilter } from 'src/app/common/departmentFilterUtils';
import { AiActivityFiltersModel } from 'src/app/models/filters/implementations/pageFilters/aiActivityFilters.model';
import { AiAgentStepsHttpService } from 'src/app/services/httpServices/detection/aiAgentStepsHttpService/ai-agent-steps-http.service';
import { AutocompleteSearchResultsV2Model } from 'src/app/models/api/autocompleteSearchResultsV2.model';
import { DisplayCamelCaseStringPipe } from 'src/app/pipes/display-camel-case-string/display-camel-case-string.pipe';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { StoreService } from 'src/app/services/infra/store/store.service';
import { isPowerPlatformActive, getActiveAiServicesFromIntegrations } from 'src/app/common/integrationToFilterUtils';
import { AiService } from 'src/app/models/api/agentStep.model';

const DEFAULT_DEBOUNCE = 1000;
const MIN_CHARS_FOR_FILTER = 3;
@Component({
  selector: 'app-ai-activity-vertical-filters',
  templateUrl: './ai-activity-vertical-filters.component.html',
  styleUrls: ['./ai-activity-vertical-filters.component.scss'],
})
export class AiActivityVerticalFiltersComponent implements OnInit {
  @Input() public pageFilters: AiActivityFiltersModel;
  @Input() public withCategories = true;
  @Input() public isFiltersMenuOpen: boolean;
  @Output() public filtersOnMenuStateChange = new EventEmitter<boolean>();

  @Output() public optionOnSelection = new EventEmitter<ISelectOption[]>();

  public optionsInsideCategories$: BehaviorSubject<ISelectOptionGroups[]> = new BehaviorSubject<ISelectOptionGroups[]>(
    []
  );
  public expandedCategories: { [optionGroup: string]: boolean } = {};
  public selectedIds: Set<string>;
  public isOpen = true;

  public selectedOptions: ISelectOption[] = [];

  public searchEnvironmentNameValuesCtrl = new UntypedFormControl();
  public filteredEnvironmentNameValues: ISelectOption[] = [];
  public isEnvironmentNameLoading$ = new BehaviorSubject(false);
  public environmentNameFilterEnum = FilterTypesEnum.Environment;

  public searchActorValuesCtrl = new UntypedFormControl();
  public filteredActorValues: ISelectOption[] = [];
  public searchThreadIdValuesCtrl = new UntypedFormControl();
  public filteredThreadIdValues: ISelectOption[] = [];
  public isActorLoading$ = new BehaviorSubject(false);
  public isThreadIdLoading$ = new BehaviorSubject(false);
  public actorFilterEnum = FilterTypesEnum.Actor;
  public threadIdFilterEnum = FilterTypesEnum.ThreadId;
  public searchAgentValuesCtrl = new UntypedFormControl();
  public filteredAgentValues: ISelectOption[] = [];
  public isAgentLoading$ = new BehaviorSubject(false);
  public agentNameFilterEnum = FilterTypesEnum.AIAgentName;

  public searchToolOriginValuesCtrl = new UntypedFormControl();
  public filteredToolOriginValues: ISelectOption[] = [];
  public isToolOriginLoading$ = new BehaviorSubject(false);
  public toolOriginFilterEnum = FilterTypesEnum.ToolOrigin;

  public departmentFilter = createDepartmentVerticalFilter();

  public dynamicCategories: string[];

  public showChatModeFilter = signal(false);
  public showEnvironmentNameFilter = signal(true); // Default to true, hide only for ChatGPT-only environments

  constructor(
    private readonly _aiActivityHttpService: AiAgentStepsHttpService,
    private readonly _displayCamelCaseStringPipe: DisplayCamelCaseStringPipe,
    private readonly _destroyRef: DestroyRef,
    private readonly _store: StoreService
  ) {}

  ngOnInit(): void {
    this.dynamicCategories = this.pageFilters.getDynamicFilters();

    this.pageFilters
      .getAllFiltersOptions()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((options) => {
        const optionsInsideCategories: ISelectOptionGroups[] = [];
        const indexByCategory: { [key: string]: number } = {};

        options.forEach((option) => {
          if (option.categoryName) {
            if (option.categoryName in indexByCategory) {
              optionsInsideCategories[indexByCategory[option.categoryName]].options.push(option);
            } else if (!this.dynamicCategories.includes(option.categoryName)) {
              optionsInsideCategories.push({
                selectedCount: 0,
                displayName: option.categoryName,
                options: [option],
                isDynamic: false,
                isNew: false,
              });
              indexByCategory[option.categoryName] = optionsInsideCategories.length - 1;
            } else {
              optionsInsideCategories.push({
                selectedCount: 0,
                displayName: option.categoryName,
                options: [option],
                isDynamic: true,
                isNew: false,
              });
            }
          } else {
            optionsInsideCategories.push({
              options: [option],
            } as ISelectOptionGroups);
          }
        });

        this.dynamicCategories.forEach((category) => {
          optionsInsideCategories.push({
            selectedCount: 0,
            displayName: category,
            options: [],
            isDynamic: true,
          });
        });

        this.optionsInsideCategories$.next(optionsInsideCategories);
      });

    this.pageFilters.onUpdate$.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => {
      const newSelectedIds: Set<string> = new Set();

      this.pageFilters.getAllSelectedOptions().forEach((option) => {
        newSelectedIds.add(option.id);
      });
      this.selectedIds = newSelectedIds;
    });

    this._initActorFilter();
    this._initThreadIdFilter();
    this._initAgentFilter();
    this._initEnvironmentNameFilter();
    this._initToolOriginFilter();
    this._initConditionalFilters();
  }

  public expandClicked(optionGroupDisplayName: string, status: boolean) {
    this.expandedCategories[optionGroupDisplayName] = status;
  }

  public onToggleClick() {
    this.filtersOnMenuStateChange.emit(!this.isFiltersMenuOpen);
    this.isOpen = !this.isOpen;
  }

  public tablesFiltersChange(selectedOptions: ISelectOption[]) {
    this.pageFilters.updateFilterFromUserSelection(undefined, selectedOptions);
  }

  public actorSubmitted(users: ISelectOption[] = []): void {
    this.pageFilters.actorFilters.updateOptions(_.union(this.pageFilters.actorFilters.getOptions$().value, users));
    this.pageFilters.updateFilterFromUserSelection(undefined, _.union(this._getSelectedOptions(), users));
  }

  public threadIdSubmitted(users: ISelectOption[] = []): void {
    this.pageFilters.threadIdFilters.updateOptions(
      _.union(this.pageFilters.threadIdFilters.getOptions$().value, users)
    );
    this.pageFilters.updateFilterFromUserSelection(undefined, _.union(this._getSelectedOptions(), users));
  }

  public agentSubmitted(users: ISelectOption[] = []): void {
    this.pageFilters.agentFilters.updateOptions(_.union(this.pageFilters.agentFilters.getOptions$().value, users));
    this.pageFilters.updateFilterFromUserSelection(undefined, _.union(this._getSelectedOptions(), users));
  }

  public environmentNameSubmitted(environments: ISelectOption[] = []): void {
    this.pageFilters.environmentFilters.updateOptions(
      _.union(this.pageFilters.environmentFilters.getOptions$().value, environments)
    );
    this.pageFilters.updateFilterFromUserSelection(undefined, _.union(this._getSelectedOptions(), environments));
  }

  public toolOriginSubmitted(toolOrigins: ISelectOption[] = []): void {
    this.pageFilters.toolOriginFilters.updateOptions(
      _.union(this.pageFilters.toolOriginFilters.getOptions$().value, toolOrigins)
    );
    this.pageFilters.updateFilterFromUserSelection(undefined, _.union(this._getSelectedOptions(), toolOrigins));
  }

  public onSelect(id: string): void {
    if (!this.selectedIds.has(id)) {
      this.selectedIds.add(id);
    } else {
      this.selectedIds.delete(id);
    }
    this.optionOnSelection.emit(this._getSelectedOptions());
  }

  private _getSelectedOptions(): ISelectOption[] {
    const selectedIndexArray = Array.from(this.selectedIds);
    const optionsById = _.keyBy(this.pageFilters.getAllOptionsIncludingChatMode(), 'id');

    /// This is needed because there are options that can be selected but are not shown.
    const selectedOptions = selectedIndexArray.map((id: string) => optionsById[id]);

    return selectedOptions.filter((options) => typeof options !== 'undefined');
  }

  private _initFilter(
    control: UntypedFormControl,
    filter_name: string,
    filteredValues: ISelectOption[],
    loadingSubject: BehaviorSubject<boolean>,
    filterType: FilterTypesEnum,
    transformDisplayName?: (text: string) => string
  ) {
    control.valueChanges
      .pipe(
        filter((res) => res !== null && res?.length >= MIN_CHARS_FOR_FILTER),
        distinctUntilChanged(),
        debounceTime(DEFAULT_DEBOUNCE),
        tap(() => {
          filteredValues.length = 0;
          loadingSubject.next(true);
        }),
        switchMap((value) =>
          this._aiActivityHttpService.getAutocompleteOptions(value, filter_name).pipe(
            finalize(() => {
              loadingSubject.next(false);
            })
          )
        ),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe((data: AutocompleteSearchResultsV2Model[]) => {
        for (const item of data) {
          filteredValues.push({
            id: item.id,
            value: item.id,
            displayName: transformDisplayName ? transformDisplayName(item.resultText) : item.resultText,
            filterType: filterType,
            categoryName: filterType,
            withoutBeautifyDisplayName: true,
          } as ISelectOption);
        }
      });
  }

  private _initThreadIdFilter() {
    this.searchThreadIdValuesCtrl.valueChanges
      .pipe(
        filter((res) => res !== null && res?.length >= MIN_CHARS_FOR_FILTER),
        distinctUntilChanged(),
        tap(() => {
          this.filteredThreadIdValues = [];
        }),
        tap((value) => {
          this.filteredThreadIdValues.push({
            id: value,
            value: value,
            displayName: value,
            filterType: FilterTypesEnum.ThreadId,
            categoryName: FilterTypesEnum.ThreadId,
            withoutBeautifyDisplayName: true,
          } as ISelectOption);
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe();
  }

  private _initActorFilter() {
    this._initFilter(
      this.searchActorValuesCtrl,
      'actor',
      this.filteredActorValues,
      this.isActorLoading$,
      FilterTypesEnum.Actor
    );
  }

  private _initAgentFilter() {
    this._initFilter(
      this.searchAgentValuesCtrl,
      'agent',
      this.filteredAgentValues,
      this.isAgentLoading$,
      FilterTypesEnum.AIAgentName,
      (text) => this._displayCamelCaseStringPipe.transform(text)
    );
  }

  private _initEnvironmentNameFilter() {
    this._initFilter(
      this.searchEnvironmentNameValuesCtrl,
      'environment',
      this.filteredEnvironmentNameValues,
      this.isEnvironmentNameLoading$,
      FilterTypesEnum.Environment
    );
  }

  private _initToolOriginFilter() {
    this._initFilter(
      this.searchToolOriginValuesCtrl,
      'toolOrigin',
      this.filteredToolOriginValues,
      this.isToolOriginLoading$,
      FilterTypesEnum.ToolOrigin
    );
  }

  /**
   * Initializes conditional filter visibility based on active integrations
   * - Chat Mode filter: Only shown for M365/Power Platform customers
   * - Environment Name filter: Hidden for ChatGPT-only customers
   */
  private _initConditionalFilters() {
    // Combine integrations with allOptions$ (re-emits on toggle change)
    // to update conditional filter visibility reactively
    combineLatest([this._store.integrations$, this.pageFilters.getAllFiltersOptions()])
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(([integrations]) => {
        // Show Chat Mode filter only if M365/Power Platform is active and not in inline mode
        const isInlineMode =
          this.pageFilters.isUnifiedModeActive() && this.pageFilters.getCurrentToggleMode() === 'option2';
        this.showChatModeFilter.set(!isInlineMode && isPowerPlatformActive(integrations));

        // Hide Environment Name filter if only ChatGPT is used
        const activeServices = getActiveAiServicesFromIntegrations(integrations);
        const isChatGptOnly = activeServices.size === 1 && activeServices.has(AiService.chatGptEnterprise);
        this.showEnvironmentNameFilter.set(!isChatGptOnly);
      });
  }
}
